/**
 * Award Validation Schemas
 * @package @comp-sys/shared
 */
import { z } from 'zod';
import { AwardType } from '../types/awards.js';
/**
 * Distribution Rule Schema
 */
export const DistributionRuleSchema = z.object({
    id: z.string(),
    awardType: z.string().min(1, 'Award type is required'),
    method: z.enum(['FIXED_COUNT', 'CUMULATIVE_PERCENTAGE']),
    value: z.number().nonnegative('Value must be non-negative'),
    percentageMode: z.enum(['INCREMENTAL', 'CUMULATIVE']).optional(),
    rankFrom: z.number().positive().optional(),
    rankTo: z.number().positive().optional(),
    roundRule: z.enum(['UP', 'DOWN', 'NEAREST']).optional(),
    order: z.number().nonnegative(),
});
/**
 * Font Config Schema
 */
export const FontConfigSchema = z.object({
    family: z.string().min(1),
    size: z.number().positive(),
    weight: z.number().min(100).max(900),
    color: z.string().min(1),
});
/**
 * Text Element Config Schema
 */
export const TextElementConfigSchema = z.object({
    content: z.string().min(1),
    font: FontConfigSchema,
    align: z.enum(['left', 'center', 'right']),
});
/**
 * Badge Element Config Schema
 */
export const BadgeElementConfigSchema = z.object({
    awardType: z.string().min(1),
    size: z.enum(['small', 'medium', 'large']),
    opacity: z.number().min(0).max(1),
});
/**
 * Logo Element Config Schema
 */
export const LogoElementConfigSchema = z.object({
    url: z.string().url(),
    alt: z.string().min(1),
});
/**
 * QR Code Element Config Schema
 */
export const QRCodeElementConfigSchema = z.object({
    data: z.string().url(),
    size: z.number().positive(),
    errorCorrection: z.enum(['L', 'M', 'Q', 'H']),
});
// The legacy TEXT/BADGE/LOGO/QR_CODE CertificateElementSchema (type discriminator)
// was removed — it had no consumers in apps/api or apps/web. Replaced by the
// Typst-based CertificateElementSchema below (kind discriminator).
const hexColor = /^#[0-9a-fA-F]{6}$/;
export const CertificateTextElementSchema = z.object({
    id: z.string(),
    kind: z.literal('TEXT'),
    // Loosened from z.enum(CertificateFieldType values) to a free string per
    // spec §5.2: PARTICIPATION/JUDGE certificates carry field keys (certNo, …)
    // that the AWARD-only enum cannot hold. AWARD's 14 values remain valid.
    type: z.string().min(1),
    position: z.object({ x: z.number(), y: z.number() }),
    value: z.string().optional(),
    customFieldKey: z.string().optional(),
    fontSize: z.number().min(6).max(200),
    fontFamily: z.string(),
    color: z.string().regex(hexColor),
    align: z.enum(['left', 'center', 'right']),
    fontWeight: z.enum(['normal', 'bold']).optional(),
    letterSpacing: z.number().optional(),
    rotation: z.number().optional(),
});
export const CertificateImageElementSchema = z.object({
    id: z.string(),
    kind: z.literal('IMAGE'),
    position: z.object({ x: z.number(), y: z.number() }),
    storageKey: z.string(),
    width: z.number().positive(),
    height: z.number().positive(),
    opacity: z.number().min(0).max(1).optional(),
    rotation: z.number().optional(),
});
export const CertificateQrElementSchema = z.object({
    id: z.string(),
    kind: z.literal('QR'),
    position: z.object({ x: z.number(), y: z.number() }),
    width: z.number().positive(),
    height: z.number().positive(),
});
export const CertificatePhotoElementSchema = z.object({
    kind: z.literal('PHOTO'),
    id: z.string(),
    position: z.object({ x: z.number().finite(), y: z.number().finite() }),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
    shape: z.enum(['rect', 'circle']),
    borderWidth: z.number().finite().nonnegative().optional(),
    borderColor: z.string().regex(hexColor).optional(),
    rotation: z.number().finite().min(-360).max(360).optional(),
});
export const CertificateBarcodeElementSchema = z.object({
    kind: z.literal('BARCODE'),
    id: z.string(),
    position: z.object({ x: z.number().finite(), y: z.number().finite() }),
    barcodeType: z.enum(['qr', 'code128', 'code39', 'ean13']),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
    encodeTemplate: z.string().min(1),
    rotation: z.number().finite().min(-360).max(360).optional(),
});
export const CertificateElementSchema = z.discriminatedUnion('kind', [
    CertificateTextElementSchema,
    CertificateImageElementSchema,
    CertificateQrElementSchema,
    CertificatePhotoElementSchema,
    CertificateBarcodeElementSchema,
]);
export const CustomFontRefSchema = z.object({
    familyName: z.string().min(1),
});
export const TypstCertificateTemplateConfigSchema = z.object({
    type: z.literal('TYPST'),
    version: z.literal(1),
    background: z.object({
        storageKey: z.string(),
        publicUrl: z.string().optional(),
        mimeType: z.enum(['image/png', 'image/jpeg', 'application/pdf']),
        pageWidth: z.number().positive(),
        pageHeight: z.number().positive(),
    }),
    elements: z.array(CertificateElementSchema),
    fonts: z.array(CustomFontRefSchema).optional(),
    issuerName: z.string().optional(),
});
/**
 * PDF Overlay Text Element Schema
 * For positioning text on PDF certificate templates
 */
export const PdfOverlayTextElementSchema = z.object({
    id: z.string(),
    type: z.enum(['RECIPIENT_NAME', 'AWARD_TITLE', 'COMPETITION_NAME', 'DATE', 'CERTIFICATE_ID', 'TEAM_NAME', 'CUSTOM']),
    value: z.string().optional(), // For CUSTOM type
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
    fontSize: z.number().min(6).max(120),
    fontFamily: z.string(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    align: z.enum(['left', 'center', 'right']),
    bold: z.boolean().optional(),
});
/**
 * HTML Template Config Schema
 */
export const HtmlTemplateConfigSchema = z.object({
    type: z.literal('HTML'),
    html: z.string().min(1),
});
/**
 * PDF Overlay Template Config Schema
 */
export const PdfOverlayTemplateConfigSchema = z.object({
    type: z.literal('PDF_OVERLAY'),
    backgroundPdfUrl: z.string().url(),
    backgroundPdfKey: z.string().optional(),
    textElements: z.array(PdfOverlayTextElementSchema),
});
/**
 * Certificate Template Config Schema (discriminated union)
 */
export const CertificateTemplateConfigSchema = z.discriminatedUnion('type', [
    HtmlTemplateConfigSchema,
    PdfOverlayTemplateConfigSchema,
    TypstCertificateTemplateConfigSchema,
]);
/**
 * Competition Award Type Schema
 */
export const CompetitionAwardTypeSchema = z.object({
    id: z.string(),
    type: z.enum(['PRESET', 'CUSTOM']),
    presetType: z.nativeEnum(AwardType).optional(),
    customName: z.record(z.string(), z.string()).optional(),
    icon: z.string().optional(),
    color: z.string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex code')
        .optional(),
    order: z.number().nonnegative(),
});
/**
 * Awards Settings Schema
 */
export const AwardsSettingsSchema = z.object({
    awardDistributionRules: z.array(DistributionRuleSchema),
    awardTypes: z.array(CompetitionAwardTypeSchema),
    certificateTemplates: z.record(z.string(), CertificateTemplateConfigSchema),
});
//# sourceMappingURL=awards.js.map